/**
 * Website Template Service
 * Handles CRUD operations for website config templates (SUPER_ADMIN only)
 */

import { apiClient } from '@app/core';

export interface WebsiteTemplate {
  shopType: string;
  config: any; // The website configuration JSON
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class WebsiteTemplateService {
  /**
   * Create or update default template for a shop type (SUPER_ADMIN only)
   */
  async saveTemplate(shopType: string, config: any): Promise<void> {
    const logger = console;
    logger.log('📝 [TemplateService] Saving template for shop type:', shopType);

    try {
      // Ensure we use the authenticated apiClient which should have auth headers
      const response = await apiClient.post(`/api/website-templates/${shopType}`, config);
      logger.log('✅ [TemplateService] Template saved successfully');
      return response.data;
    } catch (error) {
      logger.error('❌ [TemplateService] Failed to save template:', error);
      throw error;
    }
  }

  /**
   * Get default template for a shop type
   */
  async getTemplate(shopType: string): Promise<any> {
    const logger = console;
    logger.log('🔍 [TemplateService] Fetching template for shop type:', shopType);

    try {
      const response = await apiClient.get(`/api/website-templates/${shopType}`);
      logger.log('✅ [TemplateService] Template fetched successfully');
      // Extract the actual template from the response wrapper
      return response.data.data || response.data;
    } catch (error) {
      logger.error('❌ [TemplateService] Failed to fetch template:', error);
      throw error;
    }
  }

  /**
   * Get all templates (SUPER_ADMIN only)
   */
  async getAllTemplates(): Promise<WebsiteTemplate[]> {
    const logger = console;
    logger.log('🔍 [TemplateService] Fetching all templates');

    try {
      const response = await apiClient.get('/api/website-templates');
      logger.log('✅ [TemplateService] All templates fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('❌ [TemplateService] Failed to fetch templates:', error);
      throw error;
    }
  }

  /**
   * Delete template for a shop type (SUPER_ADMIN only)
   */
  async deleteTemplate(shopType: string): Promise<void> {
    const logger = console;
    logger.log('🗑️ [TemplateService] Deleting template for shop type:', shopType);

    try {
      await apiClient.delete(`/api/website-templates/${shopType}`);
      logger.log('✅ [TemplateService] Template deleted successfully');
    } catch (error) {
      logger.error('❌ [TemplateService] Failed to delete template:', error);
      throw error;
    }
  }
}

export const websiteTemplateService = new WebsiteTemplateService();

